<template>
  <div class="worker-hire-action-column">
    <WdsTooltip v-if="isCancelled" :global="tableGlobals" left>
      <template #tooltip-content>
        {{ $t('No actions available - this contract has been cancelled.') }}
      </template>

      <WdsButtonGhost disabled>
        <WdsIcon name="overflow-menu-horizontal" :size="16" />
      </WdsButtonGhost>
    </WdsTooltip>
    <WdsMenu
      v-else
      z-index="50"
      placement="bottom-end"
      manual-trigger
      :global="tableGlobals"
      @menu:open="load"
    >
      <template #trigger="{ toggle }">
        <WdsButtonGhost :data-test="`worker-contract-row-actions-${input.id}`" @click="toggle">
          <WdsIcon name="overflow-menu-horizontal" :size="16" />
        </WdsButtonGhost>
      </template>

      <div v-if="column && !loading">
        <WdsMenuOption
          v-if="column.conversation?.viewerCanAccess"
          :href="column.conversation.url"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.CHAT" />
          {{ $t('View messages') }}
        </WdsMenuOption>

        <WdsMenuOption
          v-if="!isDraft"
          :href="laravelNamedRoute('yjob.contract', { hire: column.id })"
        >
          <WdsIcon :size="16" :name="WdsIconNameEnum.DOCUMENT_DOWNLOAD" />
          {{ $t('Download contract PDF') }}
        </WdsMenuOption>

        <template v-if="isOffered">
          <WdsMenuDivider />
          <WdsMenuOption
            v-if="column.viewerCanAcceptContract"
            theme="SUCCESS"
            :href="
              laravelNamedRoute(namedRoutes.hires.hire.view, {
                hire: column.id,
                tab: 'overview',
              }) + '?accept-contract=1'
            "
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.CHECKBOX_CHECKED" />
            {{ $t('Accept and sign contract') }}
          </WdsMenuOption>
        </template>

        <template v-if="isOffered">
          <WdsMenuDivider />
          <WdsMenuOption
            theme="DESTRUCTIVE"
            :href="
              laravelNamedRoute(namedRoutes.hires.hire.view, {
                hire: column.id,
                tab: 'overview',
                'decline-job': 1,
              })
            "
          >
            <WdsIcon :size="16" :name="WdsIconNameEnum.CLOSE_OUTLINE" />
            {{ $t('Decline job') }}
          </WdsMenuOption>
        </template>
      </div>
      <div
        v-else-if="loading"
        class="tw-px-4 tw-py-2"
        :data-test="'worker-contract-row-actions-loading-' + input.id"
      >
        <WdsSpinner indeterminate />
      </div>
      <div v-else class="tw-px-4 tw-py-2">
        <WdsText :text="$t('No actions found available for this contract')" />
      </div>
    </WdsMenu>
  </div>
</template>

<script setup lang="ts">
import {
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsSpinner,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { FragmentType, graphql, useFragment } from '@/gql'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { HireActiveStatus } from '@/gql/graphql'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const WorkerHireActionDataRequest = graphql(`
  fragment WorkerHireActionDataRequest on Hire {
    id
    number
    activeStatus
    viewerCanAcceptContract
    conversation {
      id
      url
      viewerCanAccess
    }
    job {
      id
      name
    }
  }
`)

const WorkerHireActionColumn = graphql(`
  fragment WorkerHireActionColumn on Hire {
    id
    activeStatus
  }
`)

const props = defineProps<{ row: FragmentType<typeof WorkerHireActionColumn> }>()

const input = computed(() => useFragment(WorkerHireActionColumn, props.row))
const { result, loading, load } = useLazyQuery(
  graphql(`
    query WorkerHireActionColumnMenu($id: ID!) {
      hire(id: $id) {
        ...WorkerHireActionDataRequest
      }
    }
  `),
  () => ({ id: input.value.id }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const column = computed(() => useFragment(WorkerHireActionDataRequest, result.value?.hire))

const isDraft = computed(() => input.value?.activeStatus === HireActiveStatus.Draft)
const isOffered = computed(() => input.value?.activeStatus === HireActiveStatus.Offered)
const isCancelled = computed(() => input.value?.activeStatus === HireActiveStatus.Cancelled)
</script>

<style scoped lang="scss">
.worker-hire-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
